import { InvalidCropRegionError } from '../errors';
import type { StitchRegion } from '../ports';

/** The pyramid facts an on-demand crop needs, as `info.json` records them. */
export interface DziLayout {
  /** Full-resolution image width, in pixels. */
  width: number;
  /** Full-resolution image height, in pixels. */
  height: number;
  tileSize: number;
}

/** A crop region in the normalized 0-1 coordinates ROI rows are stored in. */
export interface NormalizedRegion {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** One pyramid tile to fetch, and where it lands on the stitch canvas. */
export interface PlannedTile {
  z: number;
  x: number;
  y: number;
  /** Paste offset on the canvas, in pixels. */
  left: number;
  top: number;
}

/** Tuning for {@link TileCropPlanner.plan}. */
export interface TileCropOptions {
  /**
   * Square side of the crop the caller will produce, in pixels.
   *
   * Also the floor on the native box, which is what keeps a crop from ever being
   * enlarged: a box at least this wide can only be scaled down to reach it.
   */
  outputSize?: number;
  /**
   * Margin around the region, as a percentage of its longest side, added on both
   * sides. Gives an ROI some of its surroundings for context.
   */
  paddingPercent?: number;
}

export interface TileCropPlan {
  /** Pyramid level the tiles come from. 0 is the single-tile top. */
  level: number;
  /** Native pixels per level pixel: 1 at native resolution, doubling per level. */
  scale: number;
  tiles: PlannedTile[];
  canvasWidth: number;
  canvasHeight: number;
  /** The crop, in canvas pixels. */
  region: StitchRegion;
}

/** A crop box in full-resolution pixels of the image it was cut from. */
export interface PixelBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * Decides which pyramid tiles an on-demand crop needs.
 *
 * Works down from native resolution to the first level where the region spans at
 * most {@link TileCropPlanner.MAX_TILES_PER_AXIS} tiles per axis, so a crop is
 * never more than four tile downloads however large the source image is - which
 * is the property that makes this viable for whole-slide images. The price is
 * resolution: a region too large for four native tiles comes back downsampled. An
 * ROI covering a 40000x30000 slide settles at 1/64 scale on two tiles.
 *
 * **`tileSize` has to be at least twice the crop side.** A box that fits at one
 * level but not the next finer one spanned three tiles there, so it was over
 * `tileSize` wide, so it is over `tileSize / 2` here - which is the whole reason a
 * crop is only ever scaled down. Below that ratio the last levels start handing
 * back boxes narrower than the crop side, and the caller has to enlarge them.
 * `apps/visualizations` therefore does not offer a 256 tile size.
 *
 * Pure arithmetic over the pyramid geometry sharp's google layout produces:
 * level 0 is a single tile, each level doubles, and level dimensions halve with
 * a ceil at every step. The level count is derived from the image dimensions
 * rather than read from `info.json`, because the layout on disk is defined by
 * exactly this arithmetic.
 */
export class TileCropPlanner {
  /** 2x2 tiles, so a crop costs at most four fetches. */
  public static readonly MAX_TILES_PER_AXIS = 2;

  /** Square side of a crop, in pixels, and the floor on the native box. */
  public static readonly DEFAULT_OUTPUT_SIZE = 256;

  /** Margin around a region, percent of its longest side, on both sides. */
  public static readonly DEFAULT_PADDING_PERCENT = 10;

  /**
   * @param region - The crop, normalized to the full-resolution image
   * @param layout - The pyramid's dimensions and tile size
   * @param options - Crop side and padding; defaults are the constants above
   * @throws InvalidCropRegionError when the region or the layout is not usable
   */
  public static plan(
    region: NormalizedRegion,
    layout: DziLayout,
    options: TileCropOptions = {}
  ): TileCropPlan {
    this.validate(region, layout);

    const outputSize = options.outputSize ?? TileCropPlanner.DEFAULT_OUTPUT_SIZE;
    const paddingPercent = options.paddingPercent ?? TileCropPlanner.DEFAULT_PADDING_PERCENT;

    const nativeLevel = this.nativeLevel(layout);
    const box = this.nativePixelBox(region, layout, outputSize, paddingPercent);

    for (let z = nativeLevel; z >= 0; z--) {
      const scale = 2 ** (nativeLevel - z);
      const dims = this.levelDims(layout, nativeLevel - z);

      const lx0 = Math.floor(box.x0 / scale);
      const ly0 = Math.floor(box.y0 / scale);
      const lx1 = Math.min(dims.width, Math.max(lx0 + 1, Math.ceil(box.x1 / scale)));
      const ly1 = Math.min(dims.height, Math.max(ly0 + 1, Math.ceil(box.y1 / scale)));

      const tileSize = layout.tileSize;
      const tx0 = Math.floor(lx0 / tileSize);
      const tx1 = Math.floor((lx1 - 1) / tileSize);
      const ty0 = Math.floor(ly0 / tileSize);
      const ty1 = Math.floor((ly1 - 1) / tileSize);

      const withinBudget =
        tx1 - tx0 + 1 <= TileCropPlanner.MAX_TILES_PER_AXIS &&
        ty1 - ty0 + 1 <= TileCropPlanner.MAX_TILES_PER_AXIS;

      if (!withinBudget) continue;

      const tiles: PlannedTile[] = [];

      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          tiles.push({ z, x: tx, y: ty, left: (tx - tx0) * tileSize, top: (ty - ty0) * tileSize });
        }
      }

      return {
        level: z,
        scale,
        tiles,
        canvasWidth: (tx1 - tx0 + 1) * tileSize,
        canvasHeight: (ty1 - ty0 + 1) * tileSize,
        region: { left: lx0 - tx0 * tileSize, top: ly0 - ty0 * tileSize, width: lx1 - lx0, height: ly1 - ly0 },
      };
    }

    // Unreachable: level 0 fits inside one tile by construction, and one tile is
    // always within budget.
    throw new InvalidCropRegionError('No pyramid level can hold the requested region');
  }

  /**
   * The box a crop of `region` is cut from, in full-resolution pixels.
   *
   * The same geometry {@link plan} works from, without the pyramid arithmetic -
   * so a client holding only the image's dimensions can place the region inside
   * a crop it did not cut itself. Options must match the ones the crop was
   * produced with.
   *
   * @param region - The crop, normalized to the full-resolution image
   * @param image - The full-resolution image's dimensions, in pixels
   * @param options - Crop side and padding; defaults are the constants above
   * @throws InvalidCropRegionError when the region or the dimensions are not usable
   */
  public static cropBox(
    region: NormalizedRegion,
    image: Pick<DziLayout, 'width' | 'height'>,
    options: TileCropOptions = {}
  ): PixelBox {
    this.assertValidRegion(region);
    this.assertPositiveDimensions([image.width, image.height]);

    const outputSize = options.outputSize ?? TileCropPlanner.DEFAULT_OUTPUT_SIZE;
    const paddingPercent = options.paddingPercent ?? TileCropPlanner.DEFAULT_PADDING_PERCENT;

    return this.nativePixelBox(region, image, outputSize, paddingPercent);
  }

  /**
   * Rejects a region that no layout could make valid, so callers can fail a
   * request before paying for any lookup behind it.
   *
   * @throws InvalidCropRegionError
   */
  public static assertValidRegion(region: NormalizedRegion): void {
    const coords = [region.x0, region.x1, region.y0, region.y1];

    if (coords.some((c) => typeof c !== 'number' || !Number.isFinite(c))) {
      throw new InvalidCropRegionError('Region coordinates must be numbers');
    }

    if (coords.some((c) => c < 0 || c > 1)) {
      throw new InvalidCropRegionError('Region coordinates must be normalized to [0, 1]');
    }

    if (region.x0 > region.x1 || region.y0 > region.y1) {
      throw new InvalidCropRegionError('Region minima must not exceed their maxima');
    }
  }

  private static validate(region: NormalizedRegion, layout: DziLayout): void {
    this.assertValidRegion(region);
    this.assertPositiveDimensions([layout.width, layout.height, layout.tileSize]);
  }

  private static assertPositiveDimensions(measures: number[]): void {
    if (measures.some((m) => !Number.isInteger(m) || m <= 0)) {
      throw new InvalidCropRegionError('Pyramid layout must have positive integer dimensions');
    }
  }

  /** Halvings from full resolution until both dimensions fit one tile. */
  private static nativeLevel(layout: DziLayout): number {
    let { width, height } = layout;
    let level = 0;

    while (width > layout.tileSize || height > layout.tileSize) {
      width = Math.ceil(width / 2);
      height = Math.ceil(height / 2);
      level += 1;
    }

    return level;
  }

  /**
   * Dimensions after `halvings` ceil-halvings - stepwise on purpose, because
   * `ceil(ceil(w/2)/2)` and `ceil(w/4)` disagree on some widths and the tiles on
   * disk follow the stepwise rule.
   */
  private static levelDims(layout: DziLayout, halvings: number): { width: number; height: number } {
    let { width, height } = layout;

    for (let i = 0; i < halvings; i++) {
      width = Math.ceil(width / 2);
      height = Math.ceil(height / 2);
    }

    return { width, height };
  }

  /**
   * The region as a square of full-resolution pixels, padded and floored.
   *
   * Squaring is what keeps the two axes off each other's back. The level is shared,
   * so with an oblong box the axis that needs a coarser level drags the other one
   * down with it - a region half the width of a 6000x4000 image used to come back
   * 376x250, short of the crop side on one axis only. A square box needs the same
   * level on both, so whatever level it lands on leaves both axes the same size.
   *
   * The floor subsumes what used to be a separate point fallback: a point has no
   * width on either axis, so its square side is zero until the floor lifts it, and
   * it lands on exactly the crop side it landed on before.
   */
  private static nativePixelBox(
    region: NormalizedRegion,
    layout: Pick<DziLayout, 'width' | 'height'>,
    outputSize: number,
    paddingPercent: number
  ): PixelBox {
    const x0 = Math.floor(region.x0 * layout.width);
    const x1 = Math.floor(region.x1 * layout.width);
    const y0 = Math.floor(region.y0 * layout.height);
    const y1 = Math.floor(region.y1 * layout.height);

    const padded = Math.round(Math.max(x1 - x0, y1 - y0) * (1 + (2 * paddingPercent) / 100));
    const side = Math.max(padded, outputSize);

    const [bx0, bx1] = this.centredSpan((x0 + x1) / 2, side, layout.width);
    const [by0, by1] = this.centredSpan((y0 + y1) / 2, side, layout.height);

    return { x0: bx0, x1: bx1, y0: by0, y1: by1 };
  }

  /**
   * `side` pixels centred on `centre`, shifted inside `[0, size]` from whichever
   * edge it overhangs and clamped to the whole axis if it still does not fit.
   *
   * A clamp is the one thing that can leave the box oblong - an ROI covering a
   * whole image, or any ROI on an image thinner than the crop side. The caller
   * fits such a crop to a square rather than stretching it.
   */
  private static centredSpan(centre: number, side: number, size: number): [number, number] {
    let lo = Math.round(centre - side / 2);
    let hi = lo + side;

    if (hi > size) {
      lo -= hi - size;
      hi = size;
    }

    if (lo < 0) {
      hi = Math.min(size, hi - lo);
      lo = 0;
    }

    return [lo, Math.max(lo + 1, hi)];
  }
}
